import { WorkflowHiringReadinessScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import React, { useCallback } from 'react';
import { ConfigureHiringReadinessScoreWriteBackStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessScoreWriteBackStrategy';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureHiringReadinessScoreWriteBackV2Config } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessScoreWriteBackV2Config';
import { WorkflowHiringReadinessScoreWriteBackV2Config } from 'libs/end-to-end/src/types/databases/workflows/hiring-readiness-score-write-back/workflow-hiring-readiness-score-write-back';
import { ConfigureHiringCopilotModelByJobTitle } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringCopilotModelByJobTitle';
import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { useJobTitleSuggestionsFromUniqueValues } from 'apps/sigma2-ui/src/components/workflows/useJobTitleSuggestionsFromUniqueValues';

export interface ConfigureHiringReadinessScoreWriteBackDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowHiringReadinessScoreWriteBackDetails | null;
  updateDetails: (
    details: WorkflowHiringReadinessScoreWriteBackDetails | null,
  ) => void;
  /**
   * When provided, used to derive job title (or job title group) suggestions for the model-by-job-title config.
   * If a 'job_title_group_current' column has values, job title groups are shown; otherwise 'job_title' column values are used.
   * This aligns with the runtime logic in getBasicCopilotRequestInputs.
   */
  uniqueValuesByColumn?: TransformUniqueValuesByColumnItem[];
}

export const ConfigureHiringReadinessScoreWriteBackDetails = ({
  details,
  updateDetails,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
  uniqueValuesByColumn,
}: ConfigureHiringReadinessScoreWriteBackDetailsProps) => {
  const { jobTitleSuggestions, useJobTitleGroups } =
    useJobTitleSuggestionsFromUniqueValues(uniqueValuesByColumn);

  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowHiringReadinessScoreWriteBackDetails>) => {
      updateDetails({
        type: 'hiringReadinessScoreWriteBack',
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const updateStrategy = useCallback(
    (
      strategy: null | WorkflowHiringReadinessScoreWriteBackDetails['strategy'],
    ) => {
      updateDetailsPartial({
        strategy: strategy ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'hiringReadinessScoreWriteBack',
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode) => {
      updateDetailsPartial({ mode });
    },
    [updateDetailsPartial],
  );

  const updateSkipWriteBackIfDuplicate = useCallback(
    (skipWriteBackIfDuplicate: boolean) => {
      updateDetailsPartial({ skipWriteBackIfDuplicate });
    },
    [updateDetailsPartial],
  );

  const updatePreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  const updateMinReadinessRating = useCallback(
    (minReadinessRating: number | undefined) => {
      updateDetailsPartial({
        minReadinessRating:
          minReadinessRating !== undefined && isNaN(minReadinessRating)
            ? undefined
            : minReadinessRating,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateV2Config = useCallback(
    (v2Config: WorkflowHiringReadinessScoreWriteBackV2Config | null) => {
      updateDetailsPartial({
        v2Config: v2Config ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateHiringCopilotModelByJobTitleConfig = useCallback(
    (
      hiringCopilotModelByJobTitleConfig:
        | WorkflowHiringReadinessScoreWriteBackDetails['hiringCopilotModelByJobTitleConfig']
        | null,
    ) => {
      updateDetailsPartial({
        hiringCopilotModelByJobTitleConfig:
          hiringCopilotModelByJobTitleConfig ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Hiring Readiness Score Write Back
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the hiring readiness score write back
      </S2Checkbox>
      {details && (
        <>
          <ConfigureHiringReadinessScoreWriteBackStrategy
            strategy={details.strategy ?? null}
            updateStrategy={updateStrategy}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
          />

          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
            skipWriteBackIfDuplicate={details.skipWriteBackIfDuplicate ?? false}
            updateSkipWriteBackIfDuplicate={updateSkipWriteBackIfDuplicate}
          />

          <label className="space-y-1 flex flex-col">
            <span>Number of previous days of applicants</span>

            <input
              type="text"
              value={details.numPreviousDaysOfApplicants ?? ''}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updatePreviousDaysOfApplicants(numericValue);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <label className="space-y-1 flex flex-col">
            <span>Minimum readiness rating</span>

            <input
              type="text"
              value={details.minReadinessRating ?? ''}
              onChange={(e) => {
                const numericValue = parseInt(e.target.value, 10);
                updateMinReadinessRating(numericValue);
              }}
              className="form-input border rounded p-2"
            />
          </label>
          <label className="space-y-1 flex flex-col">
            <span>Number of Samples To Send</span>

            <input
              type="number"
              value={details.numberOfSamplesToSend ?? ''}
              onChange={(e) => {
                updateNumberOfSamplesToSend(e.target.valueAsNumber);
              }}
              className="form-input border rounded p-2"
            />
          </label>

          <ConfigureHiringReadinessScoreWriteBackV2Config
            v2Config={details.v2Config ?? null}
            updateV2Config={updateV2Config}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
            legacyStrategy={details.strategy}
            legacyMode={details.mode}
          />
          <ConfigureHiringCopilotModelByJobTitle
            jobTitleSuggestions={jobTitleSuggestions}
            details={
              details.hiringCopilotModelByJobTitleConfig ?? {
                usePretrainedModel: false,
                useJobTitleGroups: false,
                modelByJobTitle: [],
              }
            }
            updateDetails={updateHiringCopilotModelByJobTitleConfig}
            useJobTitleGroups={useJobTitleGroups}
          />
        </>
      )}
    </div>
  );
};
